import React, { useEffect, useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Flex,
  Button,
  Text,
  Badge,
  Divider,
  useColorModeValue,
  Icon,
  Tooltip,
  VStack,
  HStack,
  Spinner,
  useDisclosure,
  Fade,
  ScaleFade,
  SlideFade,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Back, BtnBox } from 'styles/ComponentStyle';
import { getUserPlanLog } from 'api/auth/plan';
import UseResponseToast from 'Hook/UseResponseToast';
import { useRecoilState } from 'recoil';
import { adminState } from 'config/Recoil';
import { motion } from 'framer-motion';
import {
  FaReceipt,
  FaArrowLeft,
  FaCalendarAlt,
  FaCreditCard,
  FaMoneyBillWave,
  FaCheckCircle,
  FaShoppingCart,
} from 'react-icons/fa';

const MotionBox = styled(motion.div)`
  width: 100%;
`;

const PayBox = styled(Flex)`
  padding: 20px;
  gap: 50px;
  border-radius: 12px;
  margin-bottom: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  &:hover {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  > h4 {
    font-weight: 700;
    color: #555;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 15px;
    padding: 15px;
  }
`;

const ReceiptCard = styled(Box)`
  background: ${props => props.bg};
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    transform: translateY(-5px);
  }
`;

const ReceiptHeader = styled(Box)`
  padding: 20px;
  background: linear-gradient(135deg, #6b73ff 0%, #000dff 100%);
  color: white;
`;

const MemPay = () => {
  const { id } = useParams();
  let navigate = useNavigate();
  const [Data, setData] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const responseToast = UseResponseToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('blue.500', 'blue.400');
  const altBg = useColorModeValue('#f9f9f9', 'gray.700');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const { statusCode, response, title, description, error } =
      await getUserPlanLog(id);

    responseToast({
      title,
      description,
      statusCode,
    });

    if (error) {
      setIsLoading(false);
      return;
    }

    const data = response.data.data;
    setData(data);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getPaymentMethodIcon = service => {
    switch (service) {
      case 'iamport':
      case 'kakao':
        return <Icon as={FaCreditCard} color="yellow.500" />;
      case 'innopay':
      case 'inicis':
        return <Icon as={FaCreditCard} color="blue.500" />;
      case 'nopassbook':
        return <Icon as={FaMoneyBillWave} color="green.500" />;
      default:
        return <Icon as={FaCreditCard} />;
    }
  };

  const getPaymentMethod = service => {
    if (service === 'iamport' || service === 'kakao') return '카카오페이';
    if (service === 'innopay') return '신용/체크';
    if (service === 'inicis') return '신용/체크카드 결제';
    if (service === 'nopassbook') return '무통장';
    return service;
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: i => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: 'easeOut',
      },
    }),
  };

  return (
    <ScaleFade in={true} initialScale={0.9}>
      <Box className="InfoPayContainer" p={4}>
        <BtnBox mb={6}>
          <Button
            leftIcon={<FaArrowLeft />}
            onClick={() => navigate(-1)}
            colorScheme="blue"
            variant="outline"
            size="md"
            _hover={{ bg: 'blue.50' }}
            borderRadius="full"
          >
            뒤로 가기
          </Button>
        </BtnBox>

        {isLoading ? (
          <Flex justify="center" align="center" h="300px">
            <VStack>
              <Spinner
                size="xl"
                color="blue.500"
                thickness="4px"
                speed="0.65s"
              />
              <Text mt={4} fontSize="lg" color="gray.500">
                결제 정보를 불러오는 중...
              </Text>
            </VStack>
          </Flex>
        ) : Data.length > 0 ? (
          <VStack spacing={6} align="stretch">
            <Heading as="h2" size="lg" mb={2} color="blue.600">
              결제 내역
            </Heading>
            <Divider mb={4} />

            {Data.map((item, index) => (
              <MotionBox
                key={item.tid}
                custom={index}
                initial="hidden"
                animate="visible"
                variants={cardVariants}
              >
                <ReceiptCard bg={bgColor}>
                  <ReceiptHeader bg={headerBg}>
                    <HStack justify="space-between">
                      <HStack>
                        <Icon as={FaReceipt} boxSize={6} />
                        <Heading as="h3" size="md">
                          영수증
                        </Heading>
                      </HStack>
                      <Badge
                        colorScheme="green"
                        fontSize="md"
                        p={2}
                        borderRadius="md"
                      >
                        결제완료
                      </Badge>
                    </HStack>
                  </ReceiptHeader>

                  <Box p={4}>
                    <PayBox>
                      <h4>
                        <Icon as={FaShoppingCart} mr={2} />
                        주문번호
                      </h4>
                      <Text fontWeight="medium" fontSize="md">
                        {item.tid}
                      </Text>
                    </PayBox>

                    <PayBox>
                      <h4>
                        <Icon as={FaShoppingCart} mr={2} />
                        구독 상품
                      </h4>
                      <Badge
                        colorScheme="purple"
                        p={2}
                        borderRadius="md"
                        fontSize="md"
                      >
                        {item.plan}개월 구독
                      </Badge>
                    </PayBox>

                    <PayBox style={{ backgroundColor: altBg }}>
                      <h4>
                        <Icon as={FaMoneyBillWave} mr={2} />
                        주문 총액
                      </h4>
                      <Text fontWeight="bold" fontSize="xl" color="blue.600">
                        {item.price
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        원
                      </Text>
                    </PayBox>

                    <PayBox>
                      <h4>
                        <Icon as={FaCalendarAlt} mr={2} />
                        주문일시
                      </h4>
                      <Text>
                        {dayjs(item.orderDate).format('YYYY년 MM월 DD일')}
                      </Text>
                    </PayBox>

                    <PayBox>
                      <h4>
                        <Icon as={FaCheckCircle} mr={2} />
                        주문 상태
                      </h4>
                      <Badge colorScheme="green" p={2} borderRadius="md">
                        결제완료
                      </Badge>
                    </PayBox>

                    <PayBox>
                      <h4>
                        {getPaymentMethodIcon(item.service)}
                        <Text ml={2}>결제 수단</Text>
                      </h4>
                      <HStack>
                        <Badge colorScheme="blue" p={2} borderRadius="md">
                          {getPaymentMethod(item.service)}
                        </Badge>
                      </HStack>
                    </PayBox>
                  </Box>
                </ReceiptCard>
              </MotionBox>
            ))}
          </VStack>
        ) : (
          <SlideFade in={true} offsetY="20px">
            <Box
              className="InfoContent"
              p={10}
              textAlign="center"
              borderRadius="lg"
              bg={bgColor}
              boxShadow="md"
            >
              <Icon as={FaReceipt} boxSize={12} color="gray.400" mb={4} />
              <Text fontSize="xl" color="gray.500">
                결제 내역이 없습니다.
              </Text>
            </Box>
          </SlideFade>
        )}
      </Box>
    </ScaleFade>
  );
};

export default MemPay;
